import React from 'react';
import { styled } from '@compiled/react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { useFragment, graphql } from 'react-relay';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { EmptyFieldText } from '@atlassian/jira-issue-empty-field-text/src/ui/index.tsx';
import { useBespokeEmptyFieldTextExperiment } from '@atlassian/jira-issue-experiments/src/bespoke-empty-field-text-experiment/index.tsx';
import { useUserPreferredWorkItemLayout } from '@atlassian/jira-issue-view-layout/src/services/main.tsx';
import {
	useHoverPopoverContentRef,
	type HoverPopoverContentRef,
} from '@atlassian/jira-hover-popover/src/controllers/hover-popover-content-provider/index.tsx';
import type { parent_issueFieldParentReadviewFull_ParentReadView$key as ParentFragment } from './__generated__/parent_issueFieldParentReadviewFull_ParentReadView.graphql';
import messages from './messages.tsx';
import { Summary } from './summary/index.tsx';
import type { ParentReadViewProps } from './types.tsx';

/**
 * The ParentReadView will show a read only view of Parent field.
 * @param props [ParentReadViewProps](./types.tsx)
 */
export const ParentReadView = ({
	fragmentRef,
	disableClickAction,
	emptyText,
	onClick,
	isTruncated = true,
	isSingleLineRowHeightEnabled,
}: ParentReadViewProps): React.JSX.Element => {
	const data = useFragment<ParentFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment parent_issueFieldParentReadviewFull_ParentReadView on JiraParentIssueField {
				id
				parentIssue {
					key
					issueColorField {
						color {
							colorKey
						}
					}
					issueTypeField {
						issueType {
							avatar {
								xsmall
							}
							name
						}
					}
					summaryField {
						text
					}
				}
			}
		`,
		fragmentRef,
	);
	const { parentIssue } = data;
	const { formatMessage } = useIntl();
	const { isExperimentEnabled } = useBespokeEmptyFieldTextExperiment();
	const { userPreferredLayout } = useUserPreferredWorkItemLayout();
	const noValueTextExperiment = formatMessage(messages.noValueTextExperiment);

	const issueType = parentIssue?.issueTypeField?.issueType;
	const label = `${parentIssue?.key} ${parentIssue?.summaryField?.text ?? ''}`;
	const iconUrl = issueType?.avatar?.xsmall;
	const isWideViewLayout = userPreferredLayout === 'WIDE';

	const hoverPopoverRefFactory = isSingleLineRowHeightEnabled
		? // eslint-disable-next-line  react-hooks/rules-of-hooks
			useHoverPopoverContentRef(data.id)
		: null;

	let hoverPopoverRef: HoverPopoverContentRef = null;
	if (hoverPopoverRefFactory) {
		hoverPopoverRef = hoverPopoverRefFactory({
			content: label,
		});
	}

	if (!parentIssue) {
		return <EmptyFieldText emptyText={isExperimentEnabled ? noValueTextExperiment : emptyText} />;
	}

	if (!isSingleLineRowHeightEnabled) {
		const maybeTruncatedWithTooltip = (
			<Tooltip content={label} position="bottom">
				<Inline
					alignBlock="center"
					space="space.100"
					xcss={[isTruncated && containerStyles, isWideViewLayout && wideLayoutMaxWidth]}
				>
					{iconUrl && (
						<Icon
							src={iconUrl}
							alt={formatMessage(messages.icon, {
								issueType: issueType?.name ?? '',
							})}
						/>
					)}
					<Summary
						disableClickAction={disableClickAction}
						issueKey={parentIssue.key}
						label={label}
						colorKey={parentIssue.issueColorField?.color?.colorKey}
						onClick={onClick}
						isTruncated={isTruncated}
					/>
				</Inline>
			</Tooltip>
		);
		return fg('jira-issues--truncate-parent-field-overflow') ? (
			<Box xcss={maxWidth100}>{maybeTruncatedWithTooltip}</Box>
		) : (
			maybeTruncatedWithTooltip
		);
	}

	return (
		<Inline alignBlock="center" space="space.100" xcss={containerStyles}>
			{iconUrl && (
				<Icon
					src={iconUrl}
					alt={formatMessage(messages.icon, {
						issueType: issueType?.name ?? '',
					})}
				/>
			)}
			<Summary
				disableClickAction={disableClickAction}
				issueKey={parentIssue?.key || ''}
				label={label}
				colorKey={parentIssue?.issueColorField?.color?.colorKey}
				onClick={onClick}
				isTruncated={isTruncated}
				contentRef={hoverPopoverRef}
			/>
		</Inline>
	);
};

const containerStyles = xcss({
	overflow: 'hidden',
	whiteSpace: 'nowrap',
});

const wideLayoutMaxWidth = xcss({
	maxWidth: '240px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Icon = styled.img({
	maxWidth: '16px',
});

const maxWidth100 = xcss({
	maxWidth: '100%',
});
